<script setup lang="ts">
/*
 * App shell: header + routed page + footer, and the overlays that are
 * triggered anywhere but rendered once (toasts, mini cart, quick view,
 * search palette, mobile drawer). Chromeless routes (auth, admin) drop the
 * header/footer but keep toasts and the route progress bar.
 */
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import AppHeader from './components/AppHeader.vue';
import AppFooter from './components/AppFooter.vue';
import CookieBanner from './components/CookieBanner.vue';
import AmbientBackdrop from './components/AmbientBackdrop.vue';
import ToastHost from './components/ToastHost.vue';
import RouteProgress from './components/shell/RouteProgress.vue';
import BackToTop from './components/shell/BackToTop.vue';
import BottomTabs from './components/shell/BottomTabs.vue';
import SearchOverlay from './components/shell/SearchOverlay.vue';
import MobileNav from './components/shell/MobileNav.vue';
import MiniCart from './components/cart/MiniCart.vue';
import InfoSheet from './components/catalog/InfoSheet.vue';
import { useAuthStore } from './stores/auth';
import { useCartStore } from './stores/cart';
import { useFavoritesStore } from './stores/favorites';
import { useUiStore } from './stores/ui';
import { fetchSiteMeta } from './api/cms';
import { applySiteMeta } from './utils/meta';

const auth = useAuthStore();
const cart = useCartStore();
const favorites = useFavoritesStore();
const ui = useUiStore();
const route = useRoute();

const chromeless = computed(() => Boolean(route.meta.chromeless));

// Single reactive source of truth: every cart/favorites swap is driven by
// the authenticated user's id (login, register, logout, refresh, account
// switch). The cart store keys each bucket by user id in localStorage, so a
// previous account's items can never appear under a new one.
watch(
  () => auth.user?.id ?? null,
  (id) => {
    cart.setOwner(id);
    favorites.reset();
    if (id) void favorites.fetch();
  },
  { immediate: true },
);

// Any navigation closes whatever overlay is open.
watch(() => route.fullPath, () => ui.closeAll());

onMounted(async () => {
  // Admin Meta / SEO; the built-in defaults stay when the request fails.
  fetchSiteMeta().then(applySiteMeta).catch(() => {});
  await auth.fetchUser();
});
</script>

<template>
  <div class="relative min-h-screen flex flex-col text-text-base">
    <AmbientBackdrop />
    <RouteProgress />
    <AppHeader v-if="!chromeless" />
    <main class="flex-1 overflow-x-clip" :class="chromeless ? '' : 'pb-[var(--bottom-tabs-h)] md:pb-0'">
      <RouterView />
    </main>
    <AppFooter v-if="!chromeless" />

    <template v-if="!chromeless">
      <BottomTabs />
      <MobileNav />
      <MiniCart />
      <InfoSheet />
      <SearchOverlay />
      <BackToTop />
    </template>
    <ToastHost />
    <CookieBanner />
  </div>
</template>
